import React from 'react';
import { Box, Typography } from '@drone-js/core';
import { connect, useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectConfigFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import { ReviewCardProps, ReviewReplyDisplay } from 'component/page/detail/Review/Review.selectors';
import { StringFormatter } from '@agoda/react-handyman';
import { CmsProps, CmsValue } from 'core/type/BootstrapConfig.types';
import TextToggler from 'component/common/TextToggler/TextToggler';
import ReviewsTranslateButton from 'component/page/detail/Review/ReviewTranslation/ReviewsTranslateButton';
import { getTranslatedMonthYear } from 'core-framework/util/datetime/date';
import { useLazyGetTranslationResultQuery } from 'store/api/activitiesApi';
import { emptyTranslationResult, TranslationResultResponse } from 'component/page/detail/Review/ReviewTranslation/getTranslationResultUtils';
import { selectPageTypeId } from 'store/slice/navigation';
import { TranslationResult } from 'core/type/TranslationResult.types';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import { mapStateToProps, mapDispatchToProps, Props } from 'component/page/detail/Review/ReviewTranslation/ReviewTranslation.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesReviewTranslateButtonClickEventActivitiesFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesReviewTranslateButtonClickEventActivitiesManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getElementAtIndex } from 'core-framework/util/CommonUtils';
import { isQueryLoading } from 'store/api/apiUtils';
import { measureTranslationApiResponse } from 'store/api/measurements/translationApiMeasurement';

const getReviewReplyDate = (cms: CmsValue, reply?: ReviewReplyDisplay) => {
    if (!reply) return null;
    const replyDate = reply.replyDate && getTranslatedMonthYear(cms, reply.replyDate);
    const reviewReplyDateComponent = reply && reply.replyDate && <Typography variant="caption-default">{replyDate}</Typography>;
    return StringFormatter.formatToJsx(cms[272060], reply.replyAuthorType, reviewReplyDateComponent);
};

const ReviewsDisplay: React.FC<{ isTranslated: boolean; reviewTranslation?: TranslationResult; reviewReply?: ReviewReplyDisplay } & CmsProps> = (props) => {
    const { reviewTranslation, reviewReply, cms } = props;

    if (!reviewTranslation || !props.isTranslated) return null;
    const reviewReplyDate = getReviewReplyDate(cms, reviewReply);

    return (
        <div data-element-name="activities-review-translated">
            <Typography variant="body-s" textColor="base.subtle">
                {cms[275723]}
            </Typography>
            <Typography variant="body-default-strong" textColor="base.default" mt="16">
                <AgodaMarkdown input={reviewTranslation.title} />
            </Typography>
            <Typography variant="body-s" textColor="base.default" mt="16" mb="16">
                <TextToggler text={reviewTranslation.description} defaultExpanded={true} />
            </Typography>
            {reviewTranslation.reply && (
                <Box gap="12" borderWidth={['0', '0', '0', '2']} borderStyle="solid" borderColor="base.default" ps="12" ms="16" display="flex" flexDirection="column">
                    <TextToggler text={reviewTranslation.reply} defaultExpanded={true} />
                    <Typography variant="caption-default-strong" textColor="base.subtle">
                        {reviewTranslation.reply && reviewReplyDate}
                    </Typography>
                </Box>
            )}
        </div>
    );
};

ReviewsDisplay.displayName = 'ReviewsDisplay';

const useTranslationData = (onTranslationStart: () => void, onTranslationComplete: () => void, index: number, review: ReviewCardProps) => {
    const locale = useSelector(selectLocaleFromBootstrap);
    const config = useSelector(selectConfigFromBootstrap);
    const pageTypeId = useSelector(selectPageTypeId);
    const [triggerGetTranslation, translationResponse] = useLazyGetTranslationResultQuery();
    const [translationData, setTranslationData] = React.useState<TranslationResult>(emptyTranslationResult);

    const onTranslate = () => {
        onTranslationStart();
        trackElementEvent<ActivitiesReviewTranslateButtonClickEventActivitiesFieldsV2>(
            ActivitiesReviewTranslateButtonClickEventActivitiesManifestV2.ids,
            {
                // TODO: add translation_original_language when we have from backend
                translation_translated_language: locale,
            },
            { action_element_index: index + 1 },
        );

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        triggerGetTranslation({
            reviewId: review.id,
            title: review.title,
            description: review.description,
            reply: review.replies[0]?.replyText || '',
        }).then((res) => {
            onTranslationComplete();
            const response = res as unknown as TranslationResultResponse;
            const reviewReply = getElementAtIndex(review.replies, 0);
            !isQueryLoading(response) && measureTranslationApiResponse(response, review.id, review.title, review.description, reviewReply?.replyText || '', pageTypeId, config);
            setTranslationData(res.data || emptyTranslationResult);
        });
    };

    return {
        isLoading: translationResponse.isLoading,
        isFetching: translationResponse.isFetching,
        isTranslated: translationResponse.isSuccess,

        translationData,
        onTranslate,
    };
};

const ReviewsTranslation: React.FC<Props> = (props) => {
    const reviewCopy = props.review;
    const cms = useSelector(selectCmsFromBootstrap);
    const { isLoading, isFetching, isTranslated, onTranslate, translationData } = useTranslationData(
        props.onTranslationStart,
        props.onTranslationComplete,
        props.index,
        props.review,
    );

    if (!props.review || !props.review.isTranslationRequired) {
        return null;
    }

    const onClick = () => {
        if (isLoading || isFetching) return;
        onTranslate();
    };

    return (
        <Box mt="16">
            <ReviewsTranslateButton
                isLoading={props.translationState.isLoading && props.index === props.translationState.index}
                isTranslated={isTranslated}
                isDisabled={props.translationState.isLoading && props.index !== props.translationState.index}
                onClick={onClick}
                index={props.index}
            />
            <ReviewsDisplay cms={cms} reviewReply={reviewCopy.replies[0]} isTranslated={isTranslated} reviewTranslation={translationData} />
        </Box>
    );
};
ReviewsTranslation.displayName = 'ReviewsTranslation';
export default connect(mapStateToProps, mapDispatchToProps)(ReviewsTranslation);
